import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  GitBranch,
  GripVertical,
  Lightbulb,
  Maximize2,
  MoreVertical,
  Plus,
  X,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { cn } from '../../lib/utils';
import ConditionFieldPicker, { findFieldById } from './ConditionFieldPicker';
import { buildConditionFieldCatalog } from '../../utils/conditionFieldCatalog';
import {
  SCENARIO_RECIPES,
  analyzeWorkflowContext,
  applyRecipeToBranches,
  createBranchId,
  createDefaultIfElseConfig,
  formatBranchSummary,
  getOperatorsForFieldType,
  getRecipeAvailability,
  normalizeConditionConfig,
  operatorNeedsValue,
  resolveTagOptions,
} from '../../utils/workflowConditionUtils';
import WorkflowStepEditorShell from './WorkflowStepEditorShell';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');

function CharCountInput({ value, onChange, placeholder, maxLength = 120, className, ...props }) {
  return (
    <div className="relative">
      <Input
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        maxLength={maxLength}
        className={cn('pr-10', className)}
        {...props}
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
        {(value || '').length}
      </span>
    </div>
  );
}

const IfElseConditionEditor = ({
  isOpen,
  onClose,
  onBack,
  step,
  onSave,
  token,
  agencyId,
  workflowSteps = [],
  workflowTrigger = null,
  width,
  overlayMode,
  onOverlayDismiss,
  activePanelType = 'condition',
  chromeHeight,
}) => {
  const [config, setConfig] = useState(() => normalizeConditionConfig(step?.config));
  const [tags, setTags] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [reorderMode, setReorderMode] = useState(false);

  const workflowContext = useMemo(
    () => analyzeWorkflowContext(workflowSteps, step?.id, workflowTrigger),
    [workflowSteps, step?.id, workflowTrigger],
  );

  const fieldCatalog = useMemo(
    () => buildConditionFieldCatalog(customFields),
    [customFields],
  );

  const tagOptions = useMemo(() => resolveTagOptions(tags), [tags]);

  useEffect(() => {
    if (step) {
      setConfig(normalizeConditionConfig(step.config));
    }
  }, [step]);

  useEffect(() => {
    if (!isOpen || !token) return;
    const headers = { Authorization: `Bearer ${token}` };

    const load = async () => {
      try {
        const params = new URLSearchParams();
        if (agencyId) params.append('agency_id', agencyId);
        const tagsRes = await fetch(`${API_URL}/api/workflow-actions/tags?${params}`, { headers });
        if (tagsRes.ok) {
          const data = await tagsRes.json();
          setTags(data.tags || []);
        }
      } catch {
        // fallback tags used
      }

      try {
        const params = new URLSearchParams();
        if (agencyId) params.append('agency_id', agencyId);
        const fieldsRes = await fetch(`${API_URL}/api/workflow-actions/filter-fields?${params}`, { headers });
        if (fieldsRes.ok) {
          const data = await fieldsRes.json();
          const apiCustomFields = (data.custom_fields || []).map((f) => ({
            id: f.id,
            label: f.label || f.name,
            fieldType: f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text',
          }));
          if (apiCustomFields.length) setCustomFields(apiCustomFields);
        }
      } catch {
        // catalog fallback custom fields used
      }
    };

    load();
  }, [isOpen, token, agencyId]);

  const updateBranch = useCallback((branchId, patch) => {
    setConfig((prev) => ({
      ...prev,
      branches: prev.branches.map((b) => (b.id === branchId ? { ...b, ...patch } : b)),
    }));
  }, []);

  const updateCondition = useCallback((branchId, conditionIndex, patch) => {
    setConfig((prev) => ({
      ...prev,
      branches: prev.branches.map((b) => {
        if (b.id !== branchId) return b;
        const conditions = [...b.conditions];
        conditions[conditionIndex] = { ...conditions[conditionIndex], ...patch };
        return { ...b, conditions };
      }),
    }));
  }, []);

  const addCondition = useCallback((branchId) => {
    setConfig((prev) => ({
      ...prev,
      branches: prev.branches.map((b) => {
        if (b.id !== branchId) return b;
        return {
          ...b,
          conditions: [
            ...b.conditions,
            { field: '', fieldLabel: '', operator: '', value: [], valueLabel: '' },
          ],
        };
      }),
    }));
  }, []);

  const removeCondition = useCallback((branchId, conditionIndex) => {
    setConfig((prev) => ({
      ...prev,
      branches: prev.branches.map((b) => {
        if (b.id !== branchId) return b;
        const conditions = b.conditions.filter((_, i) => i !== conditionIndex);
        return {
          ...b,
          conditions: conditions.length
            ? conditions
            : [{ field: '', fieldLabel: '', operator: '', value: [], valueLabel: '' }],
        };
      }),
    }));
  }, []);

  const addBranch = useCallback(() => {
    setConfig((prev) => ({
      ...prev,
      branches: [
        ...prev.branches,
        {
          id: createBranchId(),
          name: `Branch ${prev.branches.length + 1}`,
          collapsed: false,
          conditions: [{ field: '', fieldLabel: '', operator: '', value: [], valueLabel: '' }],
          logic: 'AND',
        },
      ],
    }));
  }, []);

  const removeBranch = useCallback((branchId) => {
    setConfig((prev) => ({
      ...prev,
      branches: prev.branches.filter((b) => b.id !== branchId),
    }));
  }, []);

  const handleRecipeChange = (recipeId) => {
    setConfig((prev) => {
      const nextBranches = recipeId === 'build_your_own'
        ? prev.branches
        : applyRecipeToBranches(recipeId, prev.branches, tagOptions);
      return { ...prev, recipe: recipeId, branches: nextBranches };
    });
  };

  const handleSave = () => {
    const hasValid = config.branches.some((b) =>
      b.conditions.some((c) => c.field && c.operator),
    );
    onSave({
      ...step,
      type: step?.type || 'if_else',
      label: config.name || 'Condition',
      name: config.name || 'Condition',
      config: {
        ...config,
        actionName: config.name,
        configured: hasValid,
        // legacy compat for runners still reading flat conditions
        conditions: config.branches[0]?.conditions || [],
        logic: (config.branches[0]?.logic || 'AND').toLowerCase(),
      },
    });
    onClose();
  };

  const renderValueInput = (branchId, condition, conditionIndex, fieldMeta) => {
    const fieldType = fieldMeta?.fieldType || 'text';
    const isTags = fieldType === 'tags' || condition.field === 'contact.tags';
    const values = Array.isArray(condition.value) ? condition.value : [];

    if (!operatorNeedsValue(condition.operator)) return null;

    if (isTags) {
      return (
        <div className="space-y-1">
          <Select
            value={values[0] || ''}
            onValueChange={(v) => {
              updateCondition(branchId, conditionIndex, {
                value: v ? [v] : [],
                valueLabel: v,
              });
            }}
          >
            <SelectTrigger className="h-9 bg-white">
              <SelectValue placeholder="Select tags" />
            </SelectTrigger>
            <SelectContent className="max-h-48">
              {tagOptions.map((tag) => (
                <SelectItem key={tag} value={tag}>{tag}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {values.length ? (
            <p className="text-xs text-slate-500">
              The condition will be true if all of the selected tags are added to the contact
            </p>
          ) : null}
        </div>
      );
    }

    if (fieldType === 'date') {
      return (
        <Input
          type="date"
          value={values[0] || ''}
          onChange={(e) => updateCondition(branchId, conditionIndex, {
            value: e.target.value ? [e.target.value] : [],
            valueLabel: e.target.value,
          })}
          className="h-9"
        />
      );
    }

    if (fieldType === 'number') {
      return (
        <Input
          type="number"
          value={values[0] || ''}
          onChange={(e) => updateCondition(branchId, conditionIndex, {
            value: e.target.value ? [e.target.value] : [],
            valueLabel: e.target.value,
          })}
          className="h-9"
          placeholder="Enter value"
        />
      );
    }

    return (
      <Input
        value={values[0] || ''}
        onChange={(e) => updateCondition(branchId, conditionIndex, {
          value: e.target.value ? [e.target.value] : [],
          valueLabel: e.target.value,
        })}
        className="h-9"
        placeholder="Enter value"
      />
    );
  };

  const renderConditionRow = (branch, condition, conditionIndex) => {
    const fieldMeta = condition.field ? findFieldById(fieldCatalog, condition.field) : null;
    const fieldType = fieldMeta?.fieldType || (condition.field === 'contact.tags' ? 'tags' : 'text');
    const operators = getOperatorsForFieldType(fieldType);

    return (
      <div key={`${branch.id}-${conditionIndex}`} className="space-y-2">
        {conditionIndex > 0 ? (
          <div className="flex items-center gap-2">
            <Select
              value={branch.logic || 'AND'}
              onValueChange={(v) => updateBranch(branch.id, { logic: v })}
            >
              <SelectTrigger className="h-8 w-24 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AND">AND</SelectItem>
                <SelectItem value="OR">OR</SelectItem>
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={() => addCondition(branch.id)}
              className="flex h-8 w-8 items-center justify-center rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
            >
              <Plus size={14} />
            </button>
          </div>
        ) : null}

        <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/50 p-3">
          <ConditionFieldPicker
            catalog={fieldCatalog}
            value={condition.field}
            workflowContext={workflowContext}
            onChange={(fieldId, field) => {
              const ops = getOperatorsForFieldType(field.fieldType);
              updateCondition(branch.id, conditionIndex, {
                field: fieldId,
                fieldLabel: field.label,
                operator: ops[0]?.value || '',
                value: [],
                valueLabel: '',
              });
            }}
          />

          {condition.field ? (
            <Select
              value={condition.operator || ''}
              onValueChange={(v) => updateCondition(branch.id, conditionIndex, { operator: v })}
            >
              <SelectTrigger className="h-9 bg-white">
                <SelectValue placeholder="Select operator" />
              </SelectTrigger>
              <SelectContent>
                {operators.map((op) => (
                  <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}

          {condition.field && condition.operator
            ? renderValueInput(branch.id, condition, conditionIndex, fieldMeta)
            : null}

          {branch.conditions.length > 1 ? (
            <button
              type="button"
              onClick={() => removeCondition(branch.id, conditionIndex)}
              className="text-xs text-red-500 hover:text-red-600"
            >
              Remove condition
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  if (!isOpen || !step) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <WorkflowStepEditorShell
        isOpen={isOpen}
        width={width}
        overlayMode={overlayMode}
        onOverlayDismiss={onOverlayDismiss}
        activePanelType={activePanelType}
        chromeHeight={chromeHeight}
        className="border-l border-slate-200 bg-white"
        testId="if-else-condition-editor"
      >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div className="flex items-center gap-2">
            {onBack ? (
              <button type="button" onClick={onBack} className="rounded p-1.5 hover:bg-slate-100">
                <ArrowLeft size={16} className="text-slate-600" />
              </button>
            ) : null}
            <button type="button" className="flex items-center gap-1 rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100">
              <Lightbulb size={14} />
              Learn more
            </button>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" className="rounded p-1.5 hover:bg-slate-100">
              <Maximize2 size={16} className="text-slate-500" />
            </button>
            <button type="button" onClick={onClose} className="rounded p-1.5 hover:bg-slate-100" data-testid="if-else-close">
              <X size={16} className="text-slate-500" />
            </button>
          </div>
        </div>

        <div className="border-b border-slate-200 px-5 py-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
              <GitBranch size={20} className="text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">If / else</h2>
              <p className="text-sm text-slate-500">
                Fork the Contact journey through this workflow based on conditions
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
          <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Action name
            </Label>
            <CharCountInput
              value={config.name}
              onChange={(e) => setConfig((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Condition"
              data-testid="if-else-action-name"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Scenario recipe
            </Label>
            <p className="text-xs text-slate-500">Select from one of the pre-built condition templates</p>
            <Select value={config.recipe} onValueChange={handleRecipeChange}>
              <SelectTrigger className="bg-white" data-testid="scenario-recipe-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {SCENARIO_RECIPES.map((recipe) => {
                  const { available, disabledReason } = getRecipeAvailability(recipe, workflowContext);
                  return (
                    <SelectItem
                      key={recipe.id}
                      value={recipe.id}
                      disabled={!available}
                      title={!available ? disabledReason : undefined}
                      className={cn(!available && 'opacity-50')}
                    >
                      <div>
                        <div className="font-medium">{recipe.label}</div>
                        <div className="text-xs text-slate-500">{recipe.description}</div>
                        {!available && disabledReason ? (
                          <div className="text-xs text-slate-400 mt-0.5">{disabledReason}</div>
                        ) : null}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Branches
              </Label>
              {config.branches.length > 1 ? (
                <button
                  type="button"
                  onClick={() => setReorderMode((v) => !v)}
                  className="text-xs text-slate-500 hover:text-slate-700"
                >
                  {reorderMode ? 'Done reordering' : 'Reorder branches'}
                </button>
              ) : null}
            </div>

            {config.branches.map((branch, branchIndex) => (
              <div key={branch.id} className="flex gap-2">
                <div className="flex w-5 shrink-0 items-start justify-center pt-4 text-slate-300">
                  <GripVertical size={16} />
                </div>
                <div className="flex-1 rounded-lg border border-slate-200 bg-white">
                  <div className="flex items-center gap-2 border-b border-slate-100 p-3">
                    <CharCountInput
                      value={branch.name}
                      onChange={(e) => updateBranch(branch.id, { name: e.target.value })}
                      className="h-9 flex-1"
                    />
                    <button type="button" className="rounded p-1 hover:bg-slate-100">
                      <MoreVertical size={16} className="text-slate-400" />
                    </button>
                    <button
                      type="button"
                      onClick={() => updateBranch(branch.id, { collapsed: !branch.collapsed })}
                      className="rounded p-1 hover:bg-slate-100"
                    >
                      {branch.collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                  </div>

                  {!branch.collapsed ? (
                    <div className="space-y-3 p-3">
                      {branch.conditions.map((condition, ci) =>
                        renderConditionRow(branch, condition, ci),
                      )}
                      <button
                        type="button"
                        onClick={() => addCondition(branch.id)}
                        className="text-sm text-emerald-600 hover:text-emerald-700"
                      >
                        + Add segment
                      </button>
                      {config.branches.length > 1 ? (
                        <button
                          type="button"
                          onClick={() => removeBranch(branch.id)}
                          className="block text-xs text-red-500"
                        >
                          Remove branch
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <div className="px-3 py-2 text-xs text-slate-500">
                      {formatBranchSummary(branch)}
                    </div>
                  )}
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addBranch}
              className="text-sm font-medium text-emerald-600 hover:text-emerald-700"
            >
              + Add branch
            </button>

            <div className="flex gap-2">
              <div className="flex w-5 shrink-0 items-start justify-center pt-4 text-slate-300">
                <GripVertical size={16} />
              </div>
              <div className="flex-1 rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-sm font-semibold text-slate-800">None branch</p>
                <p className="text-xs text-slate-500 mb-2">When no condition is met</p>
                <CharCountInput
                  value={config.noneBranch?.name || 'None'}
                  onChange={(e) => setConfig((prev) => ({
                    ...prev,
                    noneBranch: { ...prev.noneBranch, name: e.target.value },
                  }))}
                  className="h-9"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-5 py-4">
          <Button variant="outline" onClick={onClose} data-testid="if-else-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700" data-testid="if-else-save">
            Save action
          </Button>
        </div>
      </div>
      </WorkflowStepEditorShell>
    </TooltipProvider>
  );
};

export default IfElseConditionEditor;
export { createDefaultIfElseConfig };
